
import { Sheet, SheetRequest } from '@/model/Sheet';
import { Component, Emit, Prop } from 'vue-property-decorator';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { ApiError } from '@/model/ApiError';
import { mixins } from 'vue-class-component';
import SheetInviteMixin from '@/mixins/SheetInviteMixin';
import ToastVariant from '@/model/enums/toast/toast-variant';
import CustomModal from '@/components/modal/CustomModal.vue';
import CreateSheetModal from '@/active-components/sheet/CreateSheetModal.vue';
import InviteFormSheet from '@/components/sheet/InviteFormSheet.vue';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import SheetActionButton, {
    SheetAction,
} from '@/components/sheet/SheetActionButton.vue';

@Component({
    components: {
        CreateSheetModal,
        CustomModal,
        InviteFormSheet,
        ConfirmationModal,
        SheetActionButton,
    },
    computed: {
        ...mapGetters({
            error: 'sheetStorage/getError',
            projectSheets: 'sheetStorage/getProjectSheets',
        }),
    },
    methods: {
        ...mapMutations({
            setError: 'sheetStorage/setError',
            setProjectSheets: 'sheetStorage/setProjectSheets',
        }),
        ...mapActions({
            fetchProjectSheets: 'sheetStorage/fetchProjectSheets',
            remove: 'sheetStorage/remove',
            update: 'sheetStorage/update',
        }),
    },
})
export default class SheetModal extends mixins(SheetInviteMixin) {
    @Prop() sheet!: Sheet;

    readonly sheetActions: Array<{
        action: SheetAction;
        icon: string;
        tooltipKey: string;
        isLink: boolean;
    }> = [
        {
            action: 'update',
            icon: 'file-arrow-down',
            tooltipKey: 'sheet.popup.tooltip.update',
            isLink: false,
        },
        {
            action: 'view',
            icon: 'eye',
            tooltipKey: 'sheet.popup.tooltip.view',
            isLink: true,
        },
        {
            action: 'edit',
            icon: 'pencil',
            tooltipKey: 'sheet.popup.tooltip.edit',
            isLink: false,
        },
        {
            action: 'share',
            icon: 'box-arrow-up-right',
            tooltipKey: 'sheet.popup.tooltip.share',
            isLink: false,
        },
        {
            action: 'delete',
            icon: 'trash',
            tooltipKey: 'sheet.popup.tooltip.delete',
            isLink: false,
        },
    ];

    formData = { ...this.sheet } as Sheet;

    update!: (request: SheetRequest) => Promise<void>;

    setError!: (error: ApiError | null) => void;

    loading = true;

    fetchProjectSheets!: (eventId: number) => Promise<void | Sheet[]>;

    remove!: (sheetId: number) => Promise<void>;

    setProjectSheets!: (projectSheets: Sheet[] | null) => void;

    projectSheets!: Sheet[];

    eventId: number = +this.$route.params.id;

    mounted(): void {
        this.fetch();
    }

    fetch(): void {
        this.loading = true;
        this.fetchProjectSheets(this.eventId).finally(() => {
            this.loading = false;
        });
    }

    handleActionClick(action?: SheetAction, sheet?: Sheet): void {
        if (!action || !sheet) {
            return;
        }

        switch (action) {
            case 'update':
                this.openUpdate(sheet);
                break;
            case 'edit':
                this.openEdit(sheet);
                break;
            case 'share':
                this.openInvite(sheet);
                break;
            case 'delete':
                this.openRemove(sheet);
                break;
            default:
                break;
        }
    }

    @Emit()
    onCreateOrUpdateSuccess(isUpdate: boolean): void {
        this.fetch();
        this.$nextTick(() => {
            this.showToast(
                this.$t(
                    `sheet.notification.${isUpdate ? 'update-' : ''}successful`,
                ).toString(),
                this.$t('sheet.notification.title').toString(),
                ToastVariant.SUCCESS,
            );
        });
        this.$bvModal.hide('create-sheet-modal');
    }

    openCreate(): void {
        this.formData = { ...this.sheet };
        this.$bvModal.show('create-sheet-modal');
    }

    openEdit(sheet: Sheet): void {
        this.formData = { ...sheet };
        this.$bvModal.show('create-sheet-modal');
    }

    openInvite(sheet: Sheet): void {
        this.setError(null);
        this.formData = { ...sheet };
        this.$bvModal.show('invite-modal');
    }

    openRemove(sheet: Sheet): void {
        this.formData = { ...sheet };
        this.$bvModal.show('remove-modal');
    }

    openUpdate(sheet: Sheet): void {
        this.formData = { ...sheet };
        this.$bvModal.show('update-modal');
    }

    onRemove(): void {
        if (this.formData.id) {
            this.loading = true;
            this.remove(this.formData.id)
                .then(() => {
                    this.fetch();
                    this.$bvModal.hide('remove-modal');
                    this.showToast(
                        this.$t('sheet.popup.delete.success').toString(),
                        this.$t('sheet.popup.delete.title').toString(),
                        ToastVariant.SUCCESS,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    onUpdate(): void {
        if (this.formData.id) {
            this.loading = true;
            const updated: Sheet = {
                ...this.sheet,
                id: this.formData.id,
                name: this.formData.name,
                search: this.sheet.search ?? '',
            };
            this.update({ eventId: this.eventId, sheet: updated })
                .then(() => {
                    this.fetch();
                    this.$bvModal.hide('update-modal');
                    this.showToast(
                        this.$t('sheet.popup.update.success').toString(),
                        this.$t('sheet.popup.update.title').toString(),
                        ToastVariant.SUCCESS,
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    onInvite(email: string): void {
        this.submitSheetInvite(
            email,
            this.formData.id,
            'sheet.popup.share.success',
            'sheet.popup.share.title',
        );
    }

    closeInvite(): void {
        this.$bvModal.hide('invite-modal');
    }

    resetInvite(): void {
        this.setError(null);
    }

    destroyed(): void {
        this.setError(null);
        this.setProjectSheets(null);
    }
}
